import { ParsedUrlQuery } from 'querystring';
import { google } from '@morf/proto/struct_ts_proto';
import { Params } from './useUrlParams';
import { getNonSchedulingQueryParams } from './getNonSchedulingQueryParams';
import { getParentPageReferrer } from './getParentPageReferrer';

/**
 * Combines all event data for analytics tracking:
 * - Query parameters from parent page URL (parsed from document.referrer)
 * - Query parameters from iframe's own URL (non-scheduling params)
 * - Parent referrer URL
 * - Iframe URL
 *
 * When there are conflicting parameter names, parent parameters take precedence.
 *
 * @param query - The Next.js router query object (all iframe URL params)
 * @param schedulingParams - The parsed scheduling params from useUrlParams()
 * @param iframeUrl - The full URL of the iframe (optional, will use window.location.href if not provided)
 * @returns A Struct containing all event data, or undefined if no data exists
 */
export const getEventData = (
  query: ParsedUrlQuery,
  schedulingParams: Params,
  iframeUrl?: string
): google.protobuf.Struct | undefined => {
  const iframeParams = getNonSchedulingQueryParams(query, schedulingParams);
  const parentData = getParentPageReferrer();

  // Get the iframe URL
  // If iframeUrl is explicitly provided (even if empty string), use it
  // Only use window.location.href if iframeUrl is undefined
  const actualIframeUrl =
    iframeUrl !== undefined
      ? iframeUrl
      : typeof window !== 'undefined'
        ? window.location.href
        : undefined;

  // If we have no data at all, return undefined
  if (!iframeParams && !parentData && !actualIframeUrl) {
    return undefined;
  }

  const fields: Record<string, google.protobuf.Value> = {};

  // Step 1: Add iframe parameters first
  if (iframeParams?.fields) {
    Object.entries(iframeParams.fields).forEach(([key, value]) => {
      fields[key] = value;
    });
  }

  // Step 2: Add parent parameters (these override iframe params on conflict)
  if (parentData?.queryParams) {
    Object.entries(parentData.queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        fields[key] = new google.protobuf.Value({
          listValue: new google.protobuf.ListValue({
            values: value.map(
              (v) => new google.protobuf.Value({ stringValue: v })
            ),
          }),
        });
      } else {
        fields[key] = new google.protobuf.Value({ stringValue: value });
      }
    });
  }

  // Step 3: Add parent referrer URL
  if (parentData?.referrerUrl) {
    fields['parent_referrer_url'] = new google.protobuf.Value({
      stringValue: parentData.referrerUrl,
    });
  }

  if (parentData?.referrerDomain) {
    fields['parent_referrer_domain'] = new google.protobuf.Value({
      stringValue: parentData.referrerDomain,
    });
  }

  // Step 4: Add iframe URL
  if (actualIframeUrl) {
    fields['iframe_url'] = new google.protobuf.Value({
      stringValue: actualIframeUrl,
    });
  }

  return new google.protobuf.Struct({ fields });
};
